// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import useUploads from "@/hooks/useUploads";
import type { TyCustomer } from "@/types";
import Icon from "@/UI/atoms/Icon";
import FileInput from "@/UI/inputs/FileInput";
import ImagePickerInput from "@/UI/inputs/ImagePickerInput";
import PhoneInput from "@/UI/inputs/PhoneInput";
import SelectInput from "@/UI/inputs/SelectInput";
import TextInput from "@/UI/inputs/TextInput";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyCustomerFormDetails = {
    /** The person being edited, or `null` to add one. */
    customer?: TyCustomer | null;
    onDone: () => void;
};

type Props = { onClose: () => void; details?: unknown };

/**
 * A person. Two required fields and the rest optional, because an enquiry desk types what it has:
 * somebody rings, you get a name and a number, and the national ID arrives at the contract.
 *
 * **Centred rather than a side panel.** It is five fields long, and a full-height column with a save
 * button stranded at the bottom of three hundred empty pixels is the wrong shape for it.
 *
 * `status` only appears when editing. A new person is a `lead` by definition, and offering the
 * choice on the way in invites somebody to mark a buyer who has not bought anything.
 *
 * **The picture works differently on the way in.** A picture is a file, a file needs an owner, and
 * the owner does not exist until this form has saved once — so creating takes a file off the disk
 * and hands it to the tray after the save, and the confirm adopts it as the person's picture
 * (`adoptCustomerPicture`). Editing picks from the photographs they already have, because by then
 * there may be four of them and choosing is the job.
 */
export default function CustomerFormPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { customer, onDone } = details as TyCustomerFormDetails;

    const [picture, setPicture] = useState<string | null>(customer?.pictureFile ?? null);
    const [name, setName] = useState(customer?.name ?? "");
    const [phone, setPhone] = useState<string | undefined>(customer?.phone ?? "");
    const [email, setEmail] = useState(customer?.email ?? "");
    const [nationalId, setNationalId] = useState(customer?.nationalId ?? "");
    const [status, setStatus] = useState<string>(customer?.status ?? "lead");
    /** Creating only — the first note, written in the same breath as the name. */
    const [note, setNote] = useState("");
    const [shot, setShot] = useState<File[]>([]);

    const path = customer ? `/crm/customers/${customer._id}` : "/crm/customers";
    const { data, loading, error, succeededAt, invoke } = useFetch<{ customer: TyCustomer }>(path, {}, false);
    const enqueue = useUploads((state) => state.enqueue);

    useEffect(() => {
        if (!succeededAt) return;
        // The form never waits for an upload: the record is saved, the bytes are handed over, done.
        const created = data?.customer;
        if (!customer && created && shot.length) enqueue(shot, { model: "Customer", id: created._id, slot: "picture" });
        onClose();
        onDone();
    }, [succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = () => {
        const body: Record<string, unknown> = {
            name: name.trim(),
            phone: (phone ?? "").replace(/\s/g, ""),
            email: email.trim() || null,
            nationalId: nationalId.trim() || null,
        };
        if (customer) body.picture = picture;
        if (customer) body.status = status;
        if (!customer && note.trim()) body.notes = note.trim();

        invoke({ method: customer ? "PATCH" : "POST", data: body });
    };

    return (
        <section className="side-form side-form--center">
            <header className="side-form__header">
                <h2 className="side-form__title">{customer ? t.crm.editCustomer : t.crm.addCustomer}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                {/* Only when editing: a picture is a file, a file needs an owner, and the owner
                    does not exist until the person has been saved once. */}
                {customer && (
                    <ImagePickerInput
                        owner={{ model: "Customer", id: customer._id }}
                        slot="picture"
                        emit="id"
                        max={1}
                        value={picture ? [picture] : []}
                        onChange={(next) => setPicture(next[0] ?? null)}
                        label={t.crm.picture}
                        hint={t.crm.pictureHint}
                        disabled={loading}
                    />
                )}

                {/* Creating: a photograph off the disk. The tray takes it once the person exists. */}
                {!customer && (
                    <FileInput
                        files={shot}
                        onChange={setShot}
                        multiple={false}
                        accept="image/*"
                        label={`${t.crm.picture} · ${t.crm.pictureHint}`}
                        disabled={loading}
                    />
                )}

                <TextInput value={name} onChange={setName} label={t.crm.fields.name} disabled={loading} />
                {/* No caption: a flag and a fixed `+966` already say what the box is for. */}
                <PhoneInput value={phone} onChange={setPhone} disabled={loading} />
                <TextInput value={email} onChange={setEmail} label={t.crm.fields.email} type="email" dir="ltr" disabled={loading} />
                <TextInput value={nationalId} onChange={setNationalId} label={t.crm.fields.nationalId} dir="ltr" disabled={loading} />

                {!customer && <TextInput value={note} onChange={setNote} label={t.crm.fields.notes} multiline rows={2} disabled={loading} />}

                {customer && (
                    <SelectInput
                        value={status}
                        onChange={setStatus}
                        options={constants.customerStatuses.map((item) => ({ value: item, label: t.crm.statuses[item] }))}
                        label={t.crm.fields.status}
                        disabled={loading}
                    />
                )}

                {error && <p className="side-form__error">{error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={loading}>
                    {loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
